/**
 * EP Scroll Effects
 * Consolidated scroll-based interactions for ExcelPack theme
 * - Scroll reveal (Intersection Observer)
 * - Scroll progress bar
 * - Sticky CTA mobile
 * - Parallax (optional)
 */

class EPScrollEffects {
    constructor() {
        this.prefersReducedMotion = window.matchMedia(
            '(prefers-reduced-motion: reduce)'
        ).matches;

        this.config = {
            revealThreshold: 0.15,
            revealMargin: '0px 0px -50px 0px',
            stickyCTAThreshold: 300,
            parallaxStrength: 0.3
        };

        this.init();
    }

    init() {
        this.initScrollReveal();
        this.initScrollProgress();
        this.initStickyCTA();
        this.initImageBlurUp();

        if (!this.prefersReducedMotion) {
            this.initParallax();
        }

        this.initTestimonialsCarousel();
    }

    /* ========================================
       SCROLL REVEAL
       ======================================== */
    initScrollReveal() {
        const selectors = [
            '.faq-item',
            '.seller-card',
            '.ep-featured-product-card',
            '.ep-client-photo',
            '.ep-sectors__card',
            '.quick-path__card',
            '.reveal-item',
            '[data-reveal]'
        ];

        const elements = document.querySelectorAll(selectors.join(', '));

        if (!elements.length) return;

        if (this.prefersReducedMotion) {
            elements.forEach(el => el.classList.add('is-visible'));
            return;
        }

        const observer = new IntersectionObserver(
            (entries, obs) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('is-visible');
                        obs.unobserve(entry.target);
                    }
                });
            },
            {
                threshold: this.config.revealThreshold,
                rootMargin: this.config.revealMargin
            }
        );

        elements.forEach(el => observer.observe(el));
    }

    /* ========================================
       SCROLL PROGRESS BAR
       ======================================== */
    initScrollProgress() {
        // Use existing HTML element from theme.liquid
        const container = document.querySelector('.ep-scroll-progress');
        if (!container) return;

        const bar = container.querySelector('.ep-scroll-progress__bar');
        if (!bar) return;

        const updateProgress = function() {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const docHeight = Math.max(
                document.body.scrollHeight,
                document.documentElement.scrollHeight
            ) - window.innerHeight;

            if (docHeight <= 0) return;

            const progress = Math.min((scrollTop / docHeight) * 100, 100);
            bar.style.width = progress + '%';
        };

        window.addEventListener('scroll', updateProgress, { passive: true });
        window.addEventListener('resize', updateProgress, { passive: true });
        updateProgress();
        window.addEventListener('load', updateProgress);
    }

    /* ========================================
       STICKY CTA MOBILE
       ======================================== */
    initStickyCTA() {
        if (!document.body.classList.contains('template-index')) return;
        if (window.innerWidth > 749) return;

        const stickyCTA = document.createElement('div');
        stickyCTA.className = 'ep-sticky-cta';

        const link = document.createElement('a');
        link.href = '/collections';
        link.className = 'ep-sticky-cta__button';

        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('width', '20');
        svg.setAttribute('height', '20');
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.setAttribute('fill', 'none');
        svg.setAttribute('stroke', 'currentColor');
        svg.setAttribute('stroke-width', '2');

        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', 'M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z');
        svg.appendChild(path);

        const polyline = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
        polyline.setAttribute('points', '3.27 6.96 12 12.01 20.73 6.96');
        svg.appendChild(polyline);

        const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        line.setAttribute('x1', '12');
        line.setAttribute('y1', '22.08');
        line.setAttribute('x2', '12');
        line.setAttribute('y2', '12');
        svg.appendChild(line);

        const text = document.createTextNode('Créer mon packaging');

        link.appendChild(svg);
        link.appendChild(text);
        stickyCTA.appendChild(link);
        document.body.appendChild(stickyCTA);

        let lastScrollTop = 0;
        const threshold = this.config.stickyCTAThreshold;

        const handleScroll = () => {
            const scrollTop = window.scrollY;

            if (scrollTop > threshold && scrollTop > lastScrollTop) {
                stickyCTA.classList.add('is-visible');
            } else if (scrollTop < threshold) {
                stickyCTA.classList.remove('is-visible');
            }

            lastScrollTop = scrollTop;
        };

        window.addEventListener('scroll', this.throttle(handleScroll, 100), { passive: true });
    }

    /* ========================================
       IMAGE BLUR-UP LOADING
       ======================================== */
    initImageBlurUp() {
        const blurUpContainers = document.querySelectorAll('.ep-image-blur-up');

        blurUpContainers.forEach(container => {
            const img = container.querySelector('img');
            if (!img) return;

            // If already loaded, add class immediately
            if (img.complete && img.naturalHeight !== 0) {
                container.classList.add('is-loaded');
                return;
            }

            // Add class when image loads
            img.addEventListener('load', () => {
                container.classList.add('is-loaded');
            });

            // Handle error - still reveal to avoid broken state
            img.addEventListener('error', () => {
                container.classList.add('is-loaded');
            });
        });

        // Also handle skeleton placeholders
        const skeletons = document.querySelectorAll('.ep-image-skeleton');

        skeletons.forEach(skeleton => {
            const img = skeleton.querySelector('img');
            if (!img) return;

            if (img.complete && img.naturalHeight !== 0) {
                skeleton.classList.add('is-loaded');
                return;
            }

            img.addEventListener('load', () => {
                skeleton.classList.add('is-loaded');
            });

            img.addEventListener('error', () => {
                skeleton.classList.add('is-loaded');
            });
        });
    }

    /* ========================================
       PARALLAX (Subtle)
       ======================================== */
    initParallax() {
        const parallaxElements = document.querySelectorAll('[data-parallax]');
        if (!parallaxElements.length) return;

        const handleParallax = () => {
            const scrollTop = window.scrollY;

            parallaxElements.forEach(el => {
                const speed = parseFloat(el.dataset.parallax) || this.config.parallaxStrength;
                const rect = el.getBoundingClientRect();
                const elementTop = rect.top + scrollTop;
                const offset = (scrollTop - elementTop) * speed;

                el.style.transform = 'translateY(' + offset + 'px)';
            });
        };

        window.addEventListener('scroll', this.throttle(handleParallax, 16), { passive: true });
    }

    /* ========================================
       TESTIMONIALS CAROUSEL
       ======================================== */
    initTestimonialsCarousel() {
        const carousel = document.querySelector('.ep-testimonials-carousel');
        if (!carousel) return;

        const slides = carousel.querySelectorAll('.ep-testimonials-slide');
        const dots = carousel.querySelectorAll('.ep-testimonials-dot');

        if (slides.length <= 1) return;

        let currentSlide = 0;
        let autoplayInterval = null;
        const autoplayDelay = 6000;

        const goToSlide = (index) => {
            slides.forEach((slide, i) => {
                slide.classList.toggle('is-active', i === index);
            });
            dots.forEach((dot, i) => {
                dot.classList.toggle('is-active', i === index);
                dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
            });
            currentSlide = index;
        };

        const nextSlide = () => {
            const next = (currentSlide + 1) % slides.length;
            goToSlide(next);
        };

        const startAutoplay = () => {
            if (this.prefersReducedMotion) return;
            autoplayInterval = setInterval(nextSlide, autoplayDelay);
        };

        const stopAutoplay = () => {
            if (autoplayInterval) {
                clearInterval(autoplayInterval);
                autoplayInterval = null;
            }
        };

        dots.forEach((dot, index) => {
            dot.addEventListener('click', () => {
                stopAutoplay();
                goToSlide(index);
                startAutoplay();
            });
        });

        carousel.addEventListener('mouseenter', stopAutoplay);
        carousel.addEventListener('mouseleave', startAutoplay);
        carousel.addEventListener('focusin', stopAutoplay);
        carousel.addEventListener('focusout', startAutoplay);

        startAutoplay();
    }

    /* ========================================
       UTILITIES
       ======================================== */
    throttle(fn, wait) {
        let lastTime = 0;
        return function (...args) {
            const now = Date.now();
            if (now - lastTime >= wait) {
                lastTime = now;
                fn.apply(this, args);
            }
        };
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { new EPScrollEffects(); });
} else {
    new EPScrollEffects();
}

document.addEventListener('shopify:section:load', function() { new EPScrollEffects(); });
